import React, { useState, useRef } from 'react';
import clsx from 'clsx';
import { 
  Spinner,
  Button,
  Row,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  ButtonGroup,
  UncontrolledTooltip
} from 'reactstrap';
import { useParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { EditorProvider, useEditorState , useEditorActions, } from './editor-context'
import Toolbar from './Toolbar/Toolbar';
import { Sidebar } from './Sidebar';
import { Canvas } from './Canvas';
import BannerContainer from './BannerContainer';
import { useTranslation } from 'react-i18next';

const FetchingProgressLoaderContainer = styled.div`
  position: fixed;
  width: calc(100% - 80px);
  z-index: 1000;
`
const FetchingProgressBarWrapper = styled.div`
  background: rgba(0, 0, 0, 0.25);  
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.25), 0 1px rgba(255, 255, 255, 0.08);
`
const FetchingProgressBar = styled.div`
  height: 6px;
  width: 98%; 
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.05));
  transition: 0.4s linear;  
  transition-property: width, background-color;   
  background-color: ${props => props.barColor};
  transition: width 3s ease;
  animation-duration:  ${(props) => props.duration || '4.2s'};
  animation-name: ${props => progressMoved(props.barColor)};
`
const SpinnerWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: space-between;
  right: 0;
  color: ${props => props.color};
`
const progressMoved = (color) => keyframes`
  0% {
    width: 5%;
    background-color: ${color};
  }
  25% {
    width: 25%;
  }
  50% {
    width: 45%;
  }
  75% {
    width: 70%;
  }
  100% {
    width: 98%;
    background-color: ${color};
  }
`


export function FetchingProgressLoader({ color }) {
  return (
    <FetchingProgressLoaderContainer>
      <FetchingProgressBarWrapper className="trato-fetching-progress progress-moved pulse-animation">
        <FetchingProgressBar barColor="#3283ed" duration="0.7s" backgroundColor="#2F4553">
      </FetchingProgressBar>
      </FetchingProgressBarWrapper>
      <SpinnerWrapper className="px-2 py-2" color="#3283ed">
        <Spinner size="xs" />
      </SpinnerWrapper>
    </FetchingProgressLoaderContainer>
  )
}
const Root = styled.div`
  min-height: 100%;
  overflow: hidden;
`
const HeaderWrapper = styled.div`
  padding: 5px 10px;
  height: 40px;
  width: 100%;
`
const Container = styled.div`
  display: flex;
  height: calc(100vh);
`

const LeftSide = styled.div`
  width: calc(100% - 450px);
  min-height: 100%;
  position: relative;
`
const RightSide = styled.div`
  width: 450px;
  min-height: 100%;
`

const EditorToolbarWrapper = styled.div`
  height: 40px;
`
const EditorFooterWrapper = styled.div`
  max-width: 100% !important;
  width: 100%;
  height: 35px;
  background: #fafafa;
  border-bottom: 1px solid #e1e1e1;
`
const EditorCanvasWrapper = styled.div`
  height: ${props => props.hasFooter ? 'calc(100vh - 75px)' : 'calc(100vh - 40px)'};
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}
`
const EditorBannerWrapper = styled.div`
  position: absolute;
  width: 100%;
  top: 0;
`
const SidebarWrapper = styled.div`
  height: calc(100% - 40px);;
`


function EditorCanvas() {
  const { currentPage, totalPages, documentType } = useEditorState()
  const { nextPage, prevPage, setCurrentPage } = useEditorActions();

  const handleGoToPage = (page) => {
    setCurrentPage(page)
  }

  return (
    <>
        <EditorToolbarWrapper>
          <Toolbar />
        </EditorToolbarWrapper>
        <EditorCanvasWrapper hasFooter={documentType === 'pdf'}>
          <Canvas />
        </EditorCanvasWrapper>
        <EditorBannerWrapper>
          <BannerContainer />
        </EditorBannerWrapper>
        {documentType === 'pdf' &&
          <EditorFooterWrapper className="editor-canvas-footer d-flex justify-content-center align-items-center" style={{ paddingTop: 2 }}>
            <Button color="light" className="hover_text-decoration-underline" onClick={prevPage}><i className="fas fa-chevron-left"></i> Anterior </Button>
            <SelectPageModal buttonLabel={`${currentPage} / ${totalPages}`} currentPage={currentPage} totalPages={totalPages} onGoToPage={handleGoToPage} />
            <Button color="light" className="hover_text-decoration-underline" onClick={nextPage}>Siguiente <i className="fas fa-chevron-right"></i></Button>
          </EditorFooterWrapper>  
        }
    </>
  )
}

function SelectPageModal(props) {
  const {
    currentPage,
    totalPages,
    className,
    onGoToPage
  } = props;


  const { t } = useTranslation();
  const [modal, setModal] = useState(false);
  const inputRef = useRef(false);

  const toggle = () => setModal(!modal);

  const handleClick = () => {
    const value = inputRef.current.value;
    onGoToPage(Math.min(Math.max(value, 1), totalPages))
    toggle()
  }

  return (
    <div>
      <Button color="light" onClick={toggle}>{currentPage} / {totalPages}</Button>
      <Modal isOpen={modal} toggle={toggle} className={className}>
        <ModalHeader toggle={toggle}></ModalHeader>
        <ModalBody className="d-flex align-items-center justify-content-center">
          <Input type='number' min={1} max={totalPages} innerRef={inputRef} style={{width:100}} defaultValue={currentPage} />{' '}<strong className="ml-2">/ {totalPages}</strong> 
        </ModalBody>
        <ModalFooter>
          <ButtonGroup className="d-flex flex-grow-1">
            <Button color="primary" onClick={handleClick}>{t('Ir')}</Button>{' '}
            <Button color="secondary" onClick={toggle}>{t('Cancelar')}</Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    </div>
  );
  
}



export function TratoEditor(props) {
  const { document: doc, mode, isFetching, isSidebarOpen } = useEditorState()
  const { setIsSidebarOpen } = useEditorActions()

  const documentName = mode === 'template' ? doc.name : `${doc.documentId || ''} - ${doc.name || ''}`

  return (
    
    // <div className={`editor editor-${mode} position-relative`}>
    //   
    //   { !isFetching && <div style={{background: mode === 'template' ? " #5fa0d6" : "#f58543", height: 6}} /> }
    //   <div className="editor-header border-top-0">
    //     <span className="document-name">{truncate(doc.name, 150)}</span>
    //   </div>
    //   <div className={clsx("editor-container")}>
    //     <EditorCanvas />
    //     <Sidebar />
    //   </div>
    // </div>
    <Root className="editor-root">
      {/* <FetchingProgressLoader color="blue" />  */}
      { isFetching && <FetchingProgressLoader color="blue" /> }
      <Container fluid className="editor-root-container">
        <Row className="w-100" noGutters>
          <Col xl={8} className="editor-left-side p-0" hidden={isSidebarOpen} >
            <EditorCanvas />
          </Col>
          <Col className="editor-right-side p-0 overflow-hidden h-100vh position-relative" >
            <HeaderWrapper className="d-flex align-items-center border bg-light">
              <div>
                <div className=" d-xl-none pointer" style={{top: 10, left: 5}} onClick={() => setIsSidebarOpen(false)}>
                  <i className="fas fa-arrow-left" />
                </div>
              </div>
              <div className="d-flex justify-content-center align-items-center text-center w-100">
                <h5 className="text-truncate m-0 font-weight-bold " id="documentName">{documentName}</h5>
                <UncontrolledTooltip placement="bottom" target="documentName">{documentName}</UncontrolledTooltip>
              </div> 
            </HeaderWrapper>
            <SidebarWrapper>
            <Sidebar />
            </SidebarWrapper>
          </Col>
        </Row>
      </Container>
    </Root>
  )
}

export default function TratoEditorContainer(props) {
  const { documentId } = useParams();

  return (
    <EditorProvider {...props} documentId={documentId}>
      <TratoEditor />
    </EditorProvider>
  )
}